import { Badge } from "@minket/ui/components/badge";
import { Button } from "@minket/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { When } from "@/components/ops/when";
import { SubPage } from "@/components/page/page";
import { ErrorState, QueryView } from "@/components/page/states";
import { describeUserAgent } from "@/lib/auth";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";

export const Route = createFileRoute("/settings/sessions")({
  component: SessionsSettings,
});

type SessionItem = ResponseOf<typeof endpoints.sessions>["data"][number];

const description =
  "Revoking a session signs that browser out; the current one signs out here too.";

/** The account's sessions (`/account/sessions`): this browser marked, and each one revocable. */
function SessionsSettings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery(apiQuery(endpoints.sessions, {}));
  const revoke = useMutation({
    mutationFn: (sessionId: string) => apiRequest(endpoints.revokeSession, { sessionId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.sessions) }),
  });
  const signOut = useMutation({
    mutationFn: () => apiRequest(endpoints.signOut, {}),
    onSuccess: () => {
      queryClient.setQueryData(apiQuery(endpoints.session, {}).queryKey, {
        user: null,
        memberships: [],
      });
      void navigate({ to: "/signin", search: {}, replace: true });
    },
  });
  const failure = revoke.error ?? signOut.error;

  return (
    <SubPage title="Sessions" description={description}>
      <div className="grid min-w-0 gap-4">
        <QueryView query={query} noun="sessions">
          {(data) => (
            <Table aria-label="Sessions">
              <TableHeader>
                <TableRow>
                  <TableHead>Browser</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    pending={revoke.isPending || signOut.isPending}
                    onSignOut={() => signOut.mutate()}
                    onRevoke={() => revoke.mutate(session.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </QueryView>
        {failure ? (
          <ErrorState title="Could not change the session" error={failure} headingLevel={3} />
        ) : null}
      </div>
    </SubPage>
  );
}

function SessionRow({
  session,
  pending,
  onSignOut,
  onRevoke,
}: {
  session: SessionItem;
  pending: boolean;
  onSignOut: () => void;
  onRevoke: () => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <span className="flex flex-wrap items-center gap-2">
          {describeUserAgent(session.user_agent)}
          {session.current ? <Badge variant="outline">This browser</Badge> : null}
        </span>
      </TableCell>
      <TableCell>
        <When value={session.created_at} />
      </TableCell>
      <TableCell>
        <When value={session.last_seen_at} empty="Never" />
      </TableCell>
      <TableCell className="type-data text-[13px]">
        {session.ip ? <span className="wrap-anywhere">{session.ip}</span> : "Unknown"}
      </TableCell>
      <TableCell className="text-right">
        {session.current ? (
          <Button variant="outline" size="sm" loading={pending} onClick={onSignOut}>
            Sign out
          </Button>
        ) : (
          <Button variant="ghost" size="sm" loading={pending} onClick={onRevoke}>
            Revoke
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
